import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/feature/JsonLd";
import { blogArticles } from "@/mocks/categories";
import { articleContent } from "@/mocks/articles";

const SITE_URL = "https://pandabuy.quest";

function buildArticleSchema(slug: string, title: string, description: string, image: string) {
  const articleUrl = `${SITE_URL}/blog/${slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": image,
    "url": articleUrl,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl
    },
    "author": {
      "@type": "Organization",
      "name": "PandabuySpreadsheet Hub",
      "url": SITE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "PandabuySpreadsheet Hub",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg"
      }
    },
    "datePublished": "2026-06-29",
    "dateModified": "2026-06-29",
    "isAccessibleForFree": true,
    "about": {
      "@type": "Thing",
      "name": "Pandabuy Spreadsheet"
    }
  };
}

function buildBreadcrumbSchema(slug: string, title: string) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": SITE_URL
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": `${SITE_URL}/#blog`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": title,
        "item": `${SITE_URL}/blog/${slug}`
      }
    ]
  };
}

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const articleMeta = blogArticles.find((a) => a.slug === slug);
  const content = articleContent[slug || ""];

  if (!articleMeta || !content) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-4">Article Not Found</h1>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              Back to Homepage
              <i className="ri-arrow-left-line"></i>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={buildArticleSchema(slug || "", content.title, t(articleMeta.descKey), articleMeta.image)} />
      <JsonLd data={buildBreadcrumbSchema(slug || "", content.title)} />
      <Header />
      <main>
        <article>
          <section className="relative w-full min-h-[380px] md:min-h-[440px] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0">
              <img
                src={articleMeta.image}
                alt={t(articleMeta.titleKey)}
                title={t(articleMeta.titleKey)}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
            </div>
            <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center pt-16">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100/90 text-accent-700 text-xs font-medium mb-4">
                Beginner Friendly · 8 Min Read
              </span>
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl text-background-50 leading-tight mb-4">
                {content.title}
              </h1>
              <p className="text-sm md:text-base text-background-50/80 max-w-xl mx-auto">
                {t(articleMeta.descKey)}
              </p>
            </div>
          </section>

          <section className="w-full bg-background-50 py-12 md:py-16 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              {content.sections.map((section, idx) => (
                <div key={idx}>
                  <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-4 mt-8 first:mt-0">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p key={pIdx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {para}
                    </p>
                  ))}
                </div>
              ))}

              <div className="mt-12 p-6 md:p-8 rounded-lg bg-secondary-100/50 border border-secondary-200/50 text-center">
                <h3 className="font-heading text-lg md:text-xl text-foreground-900 mb-2">
                  Ready to Explore the Best Pandabuy Spreadsheets?
                </h3>
                <p className="text-sm text-foreground-600 mb-5">
                  Browse our complete category collection and access verified Pandabuy Spreadsheet links curated daily.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Back to PandabuySpreadsheet Hub
                  <i className="ri-arrow-left-line"></i>
                </Link>
              </div>

              <div className="mt-8 p-6 md:p-8 rounded-lg bg-accent-100/50 border border-accent-200/50 text-center">
                <h3 className="font-heading text-lg md:text-xl text-foreground-900 mb-2">
                  Start Shopping Smarter Today
                </h3>
                <p className="text-sm text-foreground-600 mb-5">
                  Access the full Pandabuy Spreadsheet collection with thousands of curated product listings.
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Open Pandabuy Sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
}